import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError, CollectionRow } from '../../api';
import { WEEKDAYS, seasonEndpoints } from '../../api-seasons';
import type { SeriesPackagePlan, SeriesPlan } from '../../api-seasons';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table } from '../../ui';
import { INPUT, dollars } from '../event/shared';

/**
 * A season's games as baseball series (D-375).
 *
 * The owner: "July 1–3 a series with Quincy, July 14–16 with Pistol Shrimp,
 * Aug 1–2 another with Quincy." **Group games into series** finds those runs —
 * games against one team on dates at most a day apart, a doubleheader counting
 * once — and makes one series each, named from a pattern. Preview first; the
 * apply does exactly what the preview showed. Games already in a series are
 * left alone unless "Regroup" is ticked. **Make a series** does it by hand for
 * the games ticked.
 */

const REASONS: Record<SeriesPlan['left_out'][number]['reason'], string> = {
  TOO_SHORT: 'on its own',
  IN_A_SERIES: 'already in a series',
  NO_OPPONENT: 'no opponent',
  NO_DATE: 'no date',
  CANCELED: 'canceled',
};

export function GroupSeriesPanel({ season, onChanged }: { season: CollectionRow; onChanged: () => void }) {
  const [pattern, setPattern] = useState('vs {opponent} · {dates}');
  const [regroup, setRegroup] = useState(false);
  const [minDates, setMinDates] = useState(2);
  const [plan, setPlan] = useState<SeriesPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const input = (dryRun: boolean) => ({ ...(pattern.trim() ? { name_pattern: pattern.trim() } : {}), min_dates: minDates, regroup, dry_run: dryRun });
  const preview = useMutation({
    mutationFn: () => seasonEndpoints.groupSeries(season.id, input(true)),
    onSuccess: (result) => {
      setPlan(result);
      setError(null);
      setDone(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const apply = useMutation({
    mutationFn: () => seasonEndpoints.groupSeries(season.id, input(false)),
    onSuccess: (result) => {
      setPlan(result);
      setDone(`${result.summary.create} made, ${result.summary.update} changed${result.switched_off.length ? `, ${result.switched_off.length} switched off` : ''}.`);
      onChanged();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const changing = plan ? plan.summary.create + plan.summary.update + plan.switched_off.length : 0;

  return (
    <Panel
      title="Group games into series"
      description="Consecutive games against the same team, on dates at most a day apart, become one series (a doubleheader counts once). The same team later in the season is another series. Games already in a series are left alone unless you regroup."
    >
      <div className="flex flex-wrap items-end gap-4 px-5 py-4">
        <Field label="Name" htmlFor="group-pattern" hint="{opponent}, {short}, {abbr}, {dates} (Jul 1–3), {games}">
          <input id="group-pattern" value={pattern} onChange={(e) => setPattern(e.target.value)} maxLength={160} className={`w-80 ${INPUT}`} />
        </Field>
        <Field label="Fewest dates" htmlFor="group-min" hint="One game on its own is a game">
          <input id="group-min" type="number" min={1} max={10} value={minDates} onChange={(e) => setMinDates(Math.max(1, Number(e.target.value) || 1))} className={`w-24 ${INPUT}`} />
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={regroup} onChange={(e) => setRegroup(e.target.checked)} />
          Regroup games already in a series
        </label>
        <div className="flex gap-2 pb-0.5">
          <Button variant="primary" disabled={preview.isPending} onClick={() => preview.mutate()}>
            {preview.isPending ? 'Checking…' : 'Preview'}
          </Button>
          <Button disabled={!plan || !plan.dry_run || changing === 0 || apply.isPending} onClick={() => apply.mutate()}>
            {apply.isPending ? 'Grouping…' : 'Apply'}
          </Button>
        </div>
      </div>
      {error ? (
        <div className="px-5 pb-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {done ? <p className="px-5 pb-3 text-sm text-ink-600">{done}</p> : null}
      {plan ? <SeriesPlanTable plan={plan} /> : null}
    </Panel>
  );
}

function SeriesPlanTable({ plan }: { plan: SeriesPlan }) {
  return (
    <div className="border-t border-line">
      <p className="px-5 pt-3 text-sm text-ink-600">
        {plan.dry_run ? 'Preview: ' : 'Done: '}
        {plan.summary.create} to make, {plan.summary.update} to change, {plan.summary.keep} as they are; {plan.summary.left_out} {plan.summary.left_out === 1 ? 'game' : 'games'} not grouped.
        {plan.switched_off.length ? ` Left empty and switched off: ${plan.switched_off.map((s) => s.name).join(', ')}.` : ''}
      </p>
      <Table headers={['Series', 'Dates', 'Games', 'What happens']}>
        {plan.series.length === 0 ? (
          <EmptyRow columns={4}>No runs of games to group.</EmptyRow>
        ) : (
          plan.series.map((change) => (
            <tr key={`${change.slug}-${change.starts_on}`} className="border-b border-line align-top last:border-0">
              <td className="px-5 py-2">
                <div className="text-ink-900">{change.name}</div>
                {change.rename_from ? <div className="text-xs text-ink-400">was {change.rename_from}</div> : null}
                <div className="font-mono text-xs text-ink-400">/series/{change.slug}</div>
              </td>
              <td className="px-5 py-2 whitespace-nowrap">{change.dates.map((date) => `${WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()]} ${date.slice(5)}`).join(', ')}</td>
              <td className="px-5 py-2">
                {change.games.map((game) => (
                  <div key={game.event_id} className="text-xs text-ink-600">
                    <Link to={`/events/${game.event_id}`} className="hover:underline">
                      {game.local_date} {game.title}
                    </Link>
                  </div>
                ))}
              </td>
              <td className="px-5 py-2">
                <Badge tone={change.action === 'CREATE' ? 'ok' : change.action === 'UPDATE' ? 'accent' : 'neutral'}>{change.action === 'CREATE' ? 'New series' : change.action === 'UPDATE' ? `Gains ${change.moving}` : 'As it is'}</Badge>
              </td>
            </tr>
          ))
        )}
      </Table>
      {plan.left_out.length ? (
        <p className="px-5 py-3 text-xs text-ink-400">
          Not grouped: {plan.left_out.map((entry) => `${entry.local_date ?? '—'} ${entry.title} (${REASONS[entry.reason]})`).join('; ')}
        </p>
      ) : null}
    </div>
  );
}

/** Tick games, name it or let the pattern name it, and they are one series ("Make a series"). */
export function MakeSeriesPanel({ season, series, onChanged }: { season: CollectionRow; series: CollectionRow[]; onChanged: () => void }) {
  const games = useQuery({ queryKey: ['season-games', season.id], queryFn: () => seasonEndpoints.games(season.id) });
  const [picked, setPicked] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<string | null>(null);
  const make = useMutation({
    mutationFn: () => seasonEndpoints.makeSeries(season.id, { event_ids: picked, ...(name.trim() ? { name: name.trim() } : {}), dry_run: false }),
    onSuccess: (result) => {
      setMade(`Made ${result.series[0]?.name ?? 'the series'}${result.switched_off.length ? `; ${result.switched_off.map((s) => s.name).join(', ')} left empty and switched off` : ''}.`);
      setPicked([]);
      setName('');
      setError(null);
      onChanged();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const seriesName = new Map(series.map((row) => [row.id, row.name]));
  const list = games.data?.games ?? [];
  const toggle = (id: string) => setPicked((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  return (
    <Panel title="Make a series" description="Tick the games and make them one series. A game already in another series moves to this one; a series left with no games is switched off.">
      <div className="max-h-80 overflow-y-auto border-b border-line">
        <Table headers={['', 'Date', 'Game', 'Series now']}>
          {list.length === 0 ? (
            <EmptyRow columns={4}>{games.isLoading ? 'Loading…' : 'No games in this season.'}</EmptyRow>
          ) : (
            list.map((game) => (
              <tr key={game.event_id} className="border-b border-line last:border-0">
                <td className="px-5 py-1.5">
                  <input type="checkbox" aria-label={`Pick ${game.title}`} checked={picked.includes(game.event_id)} onChange={() => toggle(game.event_id)} />
                </td>
                <td className="px-5 py-1.5 whitespace-nowrap">
                  {game.weekday !== null ? `${WEEKDAYS[game.weekday]} ` : ''}
                  {game.local_date ?? '—'}
                </td>
                <td className="px-5 py-1.5">{game.title}</td>
                <td className="px-5 py-1.5 text-ink-500">{game.series_id ? (seriesName.get(game.series_id) ?? 'another series') : '—'}</td>
              </tr>
            ))
          )}
        </Table>
      </div>
      <div className="flex flex-wrap items-end gap-3 px-5 py-4">
        <Field label="Name" htmlFor="make-name" hint="Empty: vs {opponent} · {dates}, when the games share a team">
          <input id="make-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className={`w-80 ${INPUT}`} />
        </Field>
        <Button variant="primary" disabled={picked.length === 0 || make.isPending} onClick={() => make.mutate()}>
          {make.isPending ? 'Making…' : `Make a series of ${picked.length} ${picked.length === 1 ? 'game' : 'games'}`}
        </Button>
      </div>
      {error ? (
        <div className="px-5 pb-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {made ? <p className="px-5 pb-3 text-sm text-ink-600">{made}</p> : null}
    </Panel>
  );
}

/**
 * "Create a package for this series": every game, same seat when seated,
 * priced at a percent off the single games, made as a draft to review.
 */
export function SeriesPackageForm({ series, onDone }: { series: CollectionRow; onDone: () => void }) {
  const [code, setCode] = useState('ADULT');
  const [percent, setPercent] = useState(0);
  const [zone, setZone] = useState('');
  const [sameSeat, setSameSeat] = useState<boolean | null>(null);
  const [plan, setPlan] = useState<SeriesPackagePlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = (dryRun: boolean) => ({ ticket_type_code: code.trim() || 'ADULT', percent_off: percent, ...(zone.trim() ? { price_zone_code: zone.trim() } : {}), ...(sameSeat === null ? {} : { same_seat: sameSeat }), dry_run: dryRun });
  const preview = useMutation({
    mutationFn: () => seasonEndpoints.seriesPackage(series.id, input(true)),
    onSuccess: (result) => {
      setPlan(result);
      if (sameSeat === null) setSameSeat(result.same_seat);
      setError(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const create = useMutation({
    mutationFn: () => seasonEndpoints.seriesPackage(series.id, input(false)),
    onSuccess: (result) => {
      setPlan(result);
      onDone();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  return (
    <div className="mt-3 space-y-3 rounded-md border border-line bg-page px-4 py-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Ticket type code" htmlFor={`sp-code-${series.id}`}>
          <input id={`sp-code-${series.id}`} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={40} className={`w-28 font-mono ${INPUT}`} />
        </Field>
        <Field label="Percent off" htmlFor={`sp-pct-${series.id}`} hint="Off the single-game prices added up">
          <input id={`sp-pct-${series.id}`} type="number" min={0} max={90} step={1} value={percent} onChange={(e) => setPercent(Math.min(90, Math.max(0, Number(e.target.value) || 0)))} className={`w-24 ${INPUT}`} />
        </Field>
        <Field label="Price from zone" htmlFor={`sp-zone-${series.id}`} hint="Empty: each game's highest price">
          <input id={`sp-zone-${series.id}`} value={zone} onChange={(e) => setZone(e.target.value.toUpperCase())} maxLength={40} placeholder="BOX" className={`w-28 font-mono ${INPUT}`} />
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={sameSeat ?? true} onChange={(e) => setSameSeat(e.target.checked)} />
          Same seat every game
        </label>
        <Button onClick={() => preview.mutate()} disabled={preview.isPending}>
          {preview.isPending ? 'Checking…' : 'Preview'}
        </Button>
        <Button variant="primary" disabled={!plan || !plan.dry_run || plan.problems.length > 0 || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Creating…' : 'Create draft package'}
        </Button>
      </div>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {plan ? (
        <div className="text-sm">
          <p className="text-ink-700">
            <span className="font-semibold">{plan.name}</span> ({plan.code}): {plan.games.length} games, {dollars(plan.sum_cents)} as single tickets
            {plan.percent_off ? `, ${plan.percent_off}% off` : ''} → <span className="font-semibold">{dollars(plan.price_cents)}</span>
            {plan.same_seat ? ', same seat every game' : ''}.
          </p>
          <ul className="mt-1 text-xs text-ink-500">
            {plan.games.map((game) => (
              <li key={game.event_id}>
                {game.title}: {game.price_cents !== null ? `${dollars(game.price_cents)}${game.price_from === 'DRAFT' ? ' (draft price)' : ''}` : <span className="text-bad">{game.problem}</span>}
              </li>
            ))}
          </ul>
          {plan.problems.length ? <p className="mt-1 text-xs text-bad">{plan.problems.join(' · ')}</p> : null}
          {plan.package_id ? (
            <p className="mt-2">
              Made as a draft.{' '}
              <Link to={`/packages/${plan.package_id}`} className="font-semibold text-brand hover:underline">
                Review and put it on sale
              </Link>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
